// src/pages/dashboard/branch/menu-items/MenuItemsPage.tsx
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { motion } from 'framer-motion';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
	FiChevronDown,
	FiCoffee,
	FiFilter,
	FiGrid,
	FiMenu,
	FiMove,
	FiPlus, FiSearch,
	FiX
} from 'react-icons/fi';
import { useParams, useSearchParams } from 'react-router-dom';
import Badge from '../../../../components/ui/Badge';
import Button from '../../../../components/ui/Button';
import Skeleton from '../../../../components/ui/Skeleton';
import PaginationWithLimitSelector from '../../../../components/ui/PaginationWithLimitSelector';
import { getCategoryName } from '../../../../utils/categoryHelper';
import { MenuItem, getBranchMenuItems, getItems, itemSearchPayload, batchUpdateItemSortOrders } from '../../../../utils/supabaseClient';
import useOutsideClick from '../../../../hooks/useOutsideClick';
import usePagination from '../../../../hooks/usePagination';
import AddMenuItemModal from './components/add-item-modal/AddMenuItemModal';
import DeleteMenuItemModal from './components/DeleteMenuItemModal';
import UpdateMenuItemModal from './components/update-item-modal/updateMenuItemModal';

import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { selectCategories } from '../../../../store/selectors';
import { setHasChanges } from '../../../../store/slices/branchSlice';
import { fetchCategories } from '../../../../store/slices/categorySlice';
import MenuItemGridCard from './components/MenuItemGridCard';
import MenuItemListCard from './components/MenuItemListCard';

const MenuItemsPage: React.FC = () => {
	const { t, i18n } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isPaginationLoading, setIsPaginationLoading] = useState(false);
	const [totalCount, setTotalCount] = useState(0);

	const categoryParam = searchParams.get('category');

	// Pagination setup
	const pagination = usePagination({
		initialPage: 1,
		initialLimit: 10
	});

	// Filters state
	const [searchValue, setSearchValue] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [categoryFilter, setCategoryFilter] = useState(categoryParam || '');
	const [statusFilter, setStatusFilter] = useState('all');
	const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const filterRef = useRef<HTMLDivElement>(null);

	const dispatch = useAppDispatch()
	const categories = useAppSelector(selectCategories)
	const isDndMode = viewMode === 'list' && !!categoryFilter && searchValue.trim().length === 0 && statusFilter === 'all';

	// Modal states
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [selectedMenuItem, setSelectedMenuItem] = useState<MenuItem | null>(null);

	const { branchId } = useParams<{ branchId: string }>();

	// Load menu items with server-side filtering and pagination
	const loadMenuItems = useCallback(async () => {
		if (!branchId) return;

		setIsLoading(true);
		setIsPaginationLoading(true);

		try {
			const hasSearchTerm = debouncedSearch.trim().length > 0;
			const hasAdditionalFilters = Boolean(categoryFilter || statusFilter !== 'all');
			const shouldUseSearchEndpoint = hasSearchTerm || hasAdditionalFilters;
			// In DnD mode with a category filter: load all items for the category, sorted by sort_order
			const dndActive = viewMode === 'list' && !!categoryFilter && debouncedSearch.trim().length === 0 && statusFilter === 'all';

			if (!shouldUseSearchEndpoint) {
				const result = await getBranchMenuItems(branchId, pagination.currentPage, pagination.limit);

				if (result.error) {
					console.error('Error loading menu items:', result.error);
					setMenuItems([]);
					setTotalCount(0);
				} else {
					setMenuItems(result.data ?? []);
					setTotalCount(result.count ?? 0);
				}
			} else {
				const payload: itemSearchPayload = {
					branch_id: branchId,
					category_id: categoryFilter || '',
					search: debouncedSearch,
					limit: dndActive ? 9999 : pagination.limit,
					offset: dndActive ? 0 : (pagination.currentPage - 1) * pagination.limit,
					is_available: statusFilter === 'unavailable' ? false : (statusFilter === 'all' ? null : true),
					is_featured: statusFilter === 'featured' ? true : null,
					is_published: statusFilter === 'draft' ? false : (statusFilter === 'all' ? null : true),
				};

				const result = await getItems(payload);

				if (result.error) {
					console.error('Error loading menu items:', result.error);
					setMenuItems([]);
					setTotalCount(0);
				} else if (result.data) {
					const data = dndActive
						? [...result.data].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
						: result.data;
					setMenuItems(data);
					if (!dndActive) {
						let newTotalCount = 0;
						if (result.data.length < pagination.limit) {
							newTotalCount = (pagination.currentPage - 1) * pagination.limit + result.data.length;
						} else {
							newTotalCount = pagination.currentPage * pagination.limit + 1;
						}
						setTotalCount(newTotalCount);
					} else {
						setTotalCount(data.length);
					}
				} else {
					setMenuItems([]);
					setTotalCount(0);
				}
			}
		} catch (error) {
			console.error('Error loading menu items:', error);
			setMenuItems([]);
			setTotalCount(0);
		} finally {
			setIsLoading(false);
			setIsPaginationLoading(false);
		}
	}, [branchId, pagination.currentPage, pagination.limit, debouncedSearch, categoryFilter, statusFilter, viewMode]);

	const handleDragEnd = async (result: DropResult) => {
		if (!result.destination || result.source.index === result.destination.index) return;
		const reordered = Array.from(menuItems);
		const [moved] = reordered.splice(result.source.index, 1);
		reordered.splice(result.destination.index, 0, moved);
		setMenuItems(reordered);
		await batchUpdateItemSortOrders(reordered.map((item, idx) => ({ id: item.id, sort_order: idx })));
		dispatch(setHasChanges(true));
	};

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedSearch(searchValue.trim() ? searchValue : '');
		}, 300);

		return () => clearTimeout(handler);
	}, [searchValue]);

	// Update pagination state whenever totalCount changes
	useEffect(() => {
		pagination.setTotalItems(totalCount);
	}, [totalCount, pagination.setTotalItems]);

	// Close filter dropdown when clicking outside
	useOutsideClick(filterRef, () => {
		if (isFilterOpen) {
			setIsFilterOpen(false);
		}
	});

	useEffect(() => {
		if (!branchId) return;
		dispatch(fetchCategories({ branchId }));
	}, [branchId, dispatch]);

	// Load items when any parameter changes
	useEffect(() => {
		// Reset to page 1 when filters change (except when just changing page)
		if (pagination.currentPage !== 1) {
			pagination.setCurrentPage(1);
		} else {
			loadMenuItems();
		}
	}, [debouncedSearch, categoryFilter, statusFilter, branchId]);

	// Load items when page or limit changes
	useEffect(() => {
		loadMenuItems();
	}, [pagination.currentPage, pagination.limit, loadMenuItems]);

	useEffect(() => {
		setCategoryFilter(categoryParam ?? '');
	}, [categoryParam]);

	const handleAddMenuItem = (newMenuItem: MenuItem) => {
		setIsAddModalOpen(false);
		// Refresh the current page to show the new item
		loadMenuItems();
	};

	const handleUpdateMenuItem = (updatedMenuItem: MenuItem) => {
		setIsUpdateModalOpen(false);
		setSelectedMenuItem(null);
		// Refresh the current page to show updated item
		loadMenuItems();
	};

	const handleDeleteMenuItem = (deletedItem?: MenuItem) => {
		if (!selectedMenuItem) return;

		setIsDeleteModalOpen(false);
		setSelectedMenuItem(null);
		// Refresh the current page
		loadMenuItems();
	};

	const handleEditClick = (menuItem: MenuItem) => {
		setSelectedMenuItem(menuItem);
		setIsUpdateModalOpen(true);
	};

	const handleDeleteClick = (menuItem: MenuItem) => {
		setSelectedMenuItem(menuItem);
		setIsDeleteModalOpen(true);
	};

	const handlePublishToggleUpdate = (updatedItem: MenuItem) => {
		setMenuItems((prev) => prev.map((item) =>
			item.id === updatedItem.id ? { ...item, is_published: updatedItem.is_published } : item
		));
	};

	const handleCategoryFilterChange = (categoryId: string) => {
		setCategoryFilter(categoryId);
		// Update URL params
		if (categoryId) {
			setSearchParams({ category: categoryId });
		} else {
			setSearchParams({});
		}
		// Reset to page 1 and reload
		pagination.setCurrentPage(1);
	};

	const clearCategoryFilter = () => {
		setCategoryFilter('');
		setSearchParams({});
	};

	const clearAllFilters = () => {
		setSearchValue('');
		setDebouncedSearch('');
		setCategoryFilter('');
		setStatusFilter('all');
		setSearchParams({});
		setIsFilterOpen(false);
		// Reset to page 1 and reload
		pagination.setCurrentPage(1);
	};

	// Handle pagination changes with loading state
	const handlePageChange = (page: number) => {
		setIsPaginationLoading(true);
		pagination.setCurrentPage(page);
	};

	const handleLimitChange = (limit: number) => {
		setIsPaginationLoading(true);
		pagination.setLimit(limit);
	};

	// Animation variants
	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.05
			}
		}
	};

	// Get category name by ID
	const categoryName = (categoryId: string) => {
		const cat = categories.find(cat => cat.id === categoryId)
		return cat ? getCategoryName(cat, i18n.language) : '-';
	};

	// Get selected category name for display
	const selectedCategoryName = categoryFilter ? categoryName(categoryFilter) : '';

	// Check if filters are active
	const hasFilters = searchValue || categoryFilter || statusFilter !== 'all';

	return (
		<div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
			<div className="sm:flex sm:items-center justify-between mb-6">
				<div>
					<h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('menuItems.title')}</h1>
					<p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{t('menuItems.subtitle')}</p>
					{categoryFilter && (
						<div className="mt-2 flex items-center gap-2">
							<Badge variant="primary" className="flex items-center gap-1">
								{t('menuItems.filteredByCategory')}: {selectedCategoryName}
								<button
									onClick={clearCategoryFilter}
									className="ms-1 hover:bg-primary-dark rounded-full p-0.5 cursor-pointer"
								>
									<FiX className="h-3 w-3" />
								</button>
							</Badge>
						</div>
					)}
				</div>
				<Button
					className="mt-4 sm:mt-0 w-full sm:w-fit"
					onClick={() => setIsAddModalOpen(true)}
					leftIcon={<FiPlus />}
				>
					{t('menuItems.addItem')}
				</Button>
			</div>

			<motion.div
				initial={{ scale: 0 }}
				animate={{ scale: 1 }}
				className="fixed bottom-16 end-6 sm:hidden z-50"
			>
				<Button
					size="lg"
					onClick={() => setIsAddModalOpen(true)}
					className="!rounded-full !w-14 !h-14 !p-0 shadow-lg"
				>
					<FiPlus className="w-6 h-6" />
				</Button>
			</motion.div>

			<div className="bg-white dark:bg-gray-800 shadow rounded-lg">
				<div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
					<div className="flex flex-wrap items-center gap-4">
						<div className="relative basis-full ms:basis-2/3 max-w-86">
							<div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
								<FiSearch className="h-5 w-5 text-gray-400" />
							</div>
							<input
								type="text"
								className="block w-full ps-10 pe-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
								placeholder={t('common.search')}
								value={searchValue}
								onChange={(e) => setSearchValue(e.target.value)}
							/>
						</div>

						<div className=' flex gap-2'>
							<div className="relative w-full sm:w-auto" ref={filterRef}>
								<button
									type="button"
									className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary cursor-pointer"
									onClick={() => setIsFilterOpen(!isFilterOpen)}
								>
									<FiFilter className="me-2 h-5 w-5 text-gray-500 dark:text-gray-400" />
									{t('common.filter')}
									<FiChevronDown className={`ms-1 h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
								</button>

								{isFilterOpen && (
									<motion.div
										initial={{ opacity: 0, y: -10 }}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0, y: -10 }}
										className="origin-top-right absolute right-0 mt-2 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
									>
										<div className="p-4 w-72">
											<h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
												{t('menuItems.category')}
											</h3>
											<select
												className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm cursor-pointer"
												value={categoryFilter}
												onChange={(e) => handleCategoryFilterChange(e.target.value)}
											>
												<option value="">{t('common.all')}</option>
												{categories.map((category) => (
													<option key={category.id} value={category.id} className='bg-gray-800'>
														{getCategoryName(category, i18n.language)}
													</option>
												))}
											</select>

											<h3 className="text-sm font-medium text-gray-900 dark:text-white mt-4 mb-3">
												{t('menuItems.status')}
											</h3>
											<div className="space-y-2">
												{[
													{ value: 'all', label: t('common.all') },
													{ value: 'published', label: t('menuItems.published') },
													{ value: 'draft', label: t('menuItems.draft') },
													{ value: 'available', label: t('menuItems.available') },
													{ value: 'unavailable', label: t('menuItems.unavailable') },
													{ value: 'featured', label: t('menuItems.featured') }
												].map((status) => (
													<label key={status.value} className="flex items-center">
														<input
															type="radio"
															className="form-radio h-4 w-4 text-primary border-gray-300 dark:border-gray-600 focus:ring-primary cursor-pointer"
															name="status"
															value={status.value}
															checked={statusFilter === status.value}
															onChange={() => setStatusFilter(status.value)}
														/>
														<span className="ms-2 text-sm text-gray-700 dark:text-gray-300">
															{status.label}
														</span>
													</label>
												))}
											</div>
										</div>
									</motion.div>
								)}
							</div>
							{
								hasFilters &&
								<button
									type='button'
									className='px-2 cursor-pointer bg-red-500/10 hover:bg-red-500/20 rounded-md'
									onClick={clearAllFilters}
								>
									<FiX className="h-5 w-5 text-red-500 dark:text-red-400" />
								</button>
							}
						</div>

						<div className="ms-auto flex gap-2">
							<button
								onClick={() => setViewMode('grid')}
								className={`p-2 rounded-md cursor-pointer ${viewMode === 'grid'
									? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
									: 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
									}`}
							>
								<FiGrid className="h-5 w-5" />
								<span className="sr-only">{t('menuItems.gridView')}</span>
							</button>
							<button
								onClick={() => setViewMode('list')}
								className={`p-2 rounded-md cursor-pointer ${viewMode === 'list'
									? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
									: 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
									}`}
							>
								<FiMenu className="h-5 w-5" />
								<span className="sr-only">{t('menuItems.listView')}</span>
							</button>
						</div>
					</div>
				</div>

				{isLoading || isPaginationLoading ? (
					<div className="p-6">
						{viewMode === 'grid' ? (
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
								{[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
									<div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
										<Skeleton height="h-40" width="w-full" />
										<div className="p-4">
											<Skeleton height="h-6" width="w-3/4" className="mb-3" />
											<Skeleton height="h-4" width="w-full" className="mb-2" />
											<Skeleton height="h-4" width="w-5/6" className="mb-4" />
											<div className="flex justify-between">
												<Skeleton height="h-6" width="w-16" />
												<div className="flex gap-2">
													<Skeleton height="h-8" width="w-8" rounded="rounded-full" />
													<Skeleton height="h-8" width="w-8" rounded="rounded-full" />
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						) : (
							<div className="overflow-x-auto">
								<table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
									<thead className="bg-gray-50 dark:bg-gray-700">
										<tr>
											<th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-2/5">
												<Skeleton height="h-4" width="w-22" />
											</th>
											<th scope="col" className="px-6 py-3 w-1/5">
												<Skeleton height="h-4" width="w-16" />
											</th>
											<th scope="col" className="px-6 py-3 w-1/6">
												<Skeleton height="h-4" width="w-16" />
											</th>
											<th scope="col" className="px-6 py-3 w-1/5">
												<Skeleton height="h-4" width="w-16" />
											</th>
											<th scope="col" className="px-6 py-3 w-24">
												<Skeleton height="h-4" width="w-16" />
											</th>
										</tr>
									</thead>
									<tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 ">
										{[1, 2, 3, 4, 5].map((i) => (
											<tr key={i} >
												<td className="px-6 py-4">
													<div className="flex items-center">
														<Skeleton height="h-10" width="w-10" rounded="rounded-full" className="me-3" />
														<div>
															<Skeleton height="h-5" width="w-36" className="mb-1" />
															<Skeleton height="h-4" width="w-24" />
														</div>
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap ">
													<Skeleton height="h-5" width="w-16" />
												</td>
												<td className="px-6 py-4 whitespace-nowrap ">
													<Skeleton height="h-5" width="w-16" />
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-end">
													<div className="flex justify-end gap-2">
														<Skeleton height="h-8" width="w-8" rounded="rounded-full" />
														<Skeleton height="h-8" width="w-8" rounded="rounded-full" />
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>
				) : menuItems.length === 0 ? (
					<div className="text-center py-12">
						<FiCoffee className="mx-auto h-12 w-12 text-gray-400" />
						<h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
							{t('menuItems.noMatchingItems')}
						</h3>
						<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
							{hasFilters ? t('menuItems.tryAdjustingFilters') : t('menuItems.createFirstItemDescription')}
						</p>
						{!hasFilters && (
							<div className="mt-6">
								<Button
									onClick={() => setIsAddModalOpen(true)}
									leftIcon={<FiPlus />}
								>
									{t('menuItems.addItem')}
								</Button>
							</div>
						)}
					</div>
				) : viewMode === 'grid' ? (
					<motion.div
						variants={containerVariants}
						initial="hidden"
						animate="visible"
						className="p-6"
					>
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6 auto-rows-fr">
							{menuItems.map((item) => (
								<MenuItemGridCard
									key={item.id}
									item={item}
									handleEditClick={handleEditClick}
									handleDeleteClick={handleDeleteClick}
									onPublishToggle={handlePublishToggleUpdate}
								/>
							))}
						</div>
					</motion.div>
				) : (
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
							<thead className="bg-gray-50 dark:bg-gray-700">
								<tr>
									{isDndMode && <th scope="col" className="px-3 py-3 w-10" />}
									<th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-2/5">
										{t('menuItems.name')}
									</th>
									<th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/5">
										{t('menuItems.category')}
									</th>
									<th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/6">
										{t('menuItems.price')}
									</th>
									<th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/5">
										{t('menuItems.status')}
									</th>
									<th scope="col" className="relative px-6 py-3 w-24">
										<span className="sr-only">{t('common.actions')}</span>
									</th>
								</tr>
							</thead>
							{isDndMode && (
								<thead className="bg-primary/5 dark:bg-primary/10">
									<tr>
										<td colSpan={6} className="px-4 py-2">
											<span className="flex items-center gap-1.5 text-xs text-primary font-medium">
												<FiMove className="w-3 h-3" />
												{t('settings.dragToReorder')}
											</span>
										</td>
									</tr>
								</thead>
							)}
							{isDndMode ? (
								<DragDropContext onDragEnd={handleDragEnd}>
									<Droppable droppableId="items-list">
										{(provided) => (
											<tbody
												ref={provided.innerRef}
												{...provided.droppableProps}
												className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
											>
												{menuItems.map((item, index) => (
													<Draggable key={item.id} draggableId={item.id} index={index}>
														{(provided, snapshot) => {
															return (
																<tr
																	ref={provided.innerRef}
																	{...provided.draggableProps}
																	className={`transition-colors ${snapshot.isDragging ? 'bg-primary/5 dark:bg-primary/10 shadow-lg' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}
																>
																	<td className="px-3 py-4 w-10">
																		<div
																			{...provided.dragHandleProps}
																			className="cursor-grab active:cursor-grabbing text-primary hover:text-primary/80 transition-colors p-1.5 rounded-md bg-primary/10 hover:bg-primary/20"
																		>
																			<FiMove className="w-4 h-4" />
																		</div>
																	</td>
																	<MenuItemListCard
																		item={item}
																		handleEditClick={handleEditClick}
																		handleDeleteClick={handleDeleteClick}
																		onPublishToggle={handlePublishToggleUpdate}
																		categoryName={categoryName(item.category_id || '')}
																		asCells
																	/>
																</tr>
															);
														}}
													</Draggable>
												))}
												{provided.placeholder}
											</tbody>
										)}
									</Droppable>
								</DragDropContext>
							) : (
								<tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 ">
									{menuItems.map((item: MenuItem) => (
										<MenuItemListCard
											key={item.id}
											item={item}
											handleEditClick={handleEditClick}
											handleDeleteClick={handleDeleteClick}
											onPublishToggle={handlePublishToggleUpdate}
											categoryName={categoryName(item.category_id || '')}
										/>
									))}
								</tbody>
							)}
						</table>
					</div>
				)
				}

				{/* Pagination */}
				{!isLoading && !isDndMode && totalCount > pagination.limit && (
					<div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
						<PaginationWithLimitSelector
							currentPage={pagination.currentPage}
							totalPages={Math.ceil(totalCount / pagination.limit)}
							totalItems={totalCount}
							itemsPerPage={pagination.limit}
							onPageChange={handlePageChange}
							onLimitChange={handleLimitChange}
							limitOptions={viewMode === 'grid' ? [10, 20, 30] : [10, 20, 50, 100]}
						/>
					</div>
				)}
			</div >

			{/* Modals */}
			<AddMenuItemModal
				isOpen={isAddModalOpen}
				onClose={() => setIsAddModalOpen(false)}
				onMenuItemAdded={handleAddMenuItem}
				branchId={branchId!}
				categories={categories}
				selectedCategoryId={categoryFilter}
			/>

			{selectedMenuItem && (
				<UpdateMenuItemModal
					isOpen={isUpdateModalOpen}
					onClose={() => {
						setIsUpdateModalOpen(false);
						setSelectedMenuItem(null);
					}}
					onMenuItemUpdated={handleUpdateMenuItem}
					menuItem={selectedMenuItem}
					categories={categories}
					branchId={branchId!}
				/>
			)}

			{
				selectedMenuItem && (
					<DeleteMenuItemModal
						isOpen={isDeleteModalOpen}
						onClose={() => {
							setIsDeleteModalOpen(false);
							setSelectedMenuItem(null);
						}}
						onMenuItemDeleted={handleDeleteMenuItem}
						menuItem={selectedMenuItem}
					/>
				)
			}
		</div >
	);
};

export default MenuItemsPage;